import React, { useEffect, useMemo, useRef, useState } from "react";
import UploadAndDownloadService from "../services/uploadAndDownloadFile";
import SocieteAndUserService from "../services/societeAndUser";
import { Dialog, Slide } from "@mui/material";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const GREEN = "#28a745";

// ---- presentation --------------------------------------------------------------------------------------

const Info = ({ label, value }) => (
  <div className="d-flex justify-content-between border-bottom py-1">
    <span className="text-muted mr-3">{label}</span>
    <strong className="text-right" style={{ wordBreak: "break-word" }}>
      {value === undefined || value === null || value === "" ? "—" : value}
    </strong>
  </div>
);

const Section = ({ icon, title, children }) => (
  <div className="mb-3">
    <div className="text-uppercase font-weight-bold small mb-2" style={{ color: GREEN }}>
      <i className={`fas ${icon} mr-2`} />
      {title}
    </div>
    {children}
  </div>
);

const STEP_ICON = {
  pending: <i className="far fa-circle text-muted" />,
  running: <i className="fas fa-spinner fa-spin" style={{ color: GREEN }} />,
  done: <i className="fas fa-check-circle" style={{ color: GREEN }} />,
  error: <i className="fas fa-times-circle text-danger" />,
};

// ---- the confirmation --------------------------------------------------------------------------------

// Creates the societe, then the ADMIN access attached to it and what was asked by default, in one confirmation.
const ConfirmAddSocieteWithAdmin = ({ setOnConfirmAddSociete, confirmSocieteModel, setIsCancel }) => {
  const admin = confirmSocieteModel.admin;
  const defaults = confirmSocieteModel.defaults || {};
  const [busy, setBusy] = useState(false);
  const [finished, setFinished] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [steps, setSteps] = useState({});

  // preview of the logo
  const logoUrl = useMemo(
    () => (confirmSocieteModel.file ? URL.createObjectURL(confirmSocieteModel.file) : null),
    [confirmSocieteModel.file]
  );
  useEffect(() => () => logoUrl && URL.revokeObjectURL(logoUrl), [logoUrl]);

  // What is already created survives a failure: "Réessayer" resumes at the step that failed instead of
  // creating the company a second time.
  const progress = useRef({
    fileUrl: null,
    idSociete: null,
    admin: null,
    idManager: null,
    managerDone: false,
  });
  const currentStep = useRef(null);

  const stepList = [
    { key: "emails", label: "Vérification des emails" },
    { key: "image", label: "Envoi du logo" },
    { key: "societe", label: "Création de la société" },
    { key: "admin", label: "Création de l'accès administrateur" },
    ...(defaults.manager ? [{ key: "manager", label: "Création de l'accès responsable dépôt" }] : []),
    ...(defaults.centralWarehouse || defaults.defaultCatalog
      ? [
          {
            key: "defaults",
            label:
              "Création de " +
              [defaults.centralWarehouse && "du dépôt central", defaults.defaultCatalog && "du catalogue DEFAUT"]
                .filter(Boolean)
                .join(" et "),
          },
        ]
      : []),
  ];
  const started = Object.keys(steps).length > 0;
  const somethingCreated = progress.current.idSociete !== null;

  const mark = (key, state) => setSteps((current) => ({ ...current, [key]: state }));
  const begin = (key) => {
    currentStep.current = key;
    mark(key, "running");
  };
  const fail = (key, text) => {
    mark(key, "error");
    setErrorText(text);
    setBusy(false);
  };

  const handleClose = () => {
    if (busy) return;
    setIsCancel(true);
    setOnConfirmAddSociete(false);
  };

  const messageOf = (error) => {
    const data = error && error.response && error.response.data;
    if (data && (data.error || data.message)) {
      return data.error || data.message;
    }
    return error && error.response
      ? "erreur du serveur (HTTP " + error.response.status + ")"
      : "problème de connexion";
  };

  const uploadPicture = () => {
    const data = new FormData();
    data.append("picture", confirmSocieteModel.file);
    return UploadAndDownloadService.uploadFile("/retail/uploadFile/uploadFile/SocietePicture", data);
  };

  const accessModel = (person, idSociete, fileUrl) => ({
    cin: person.cin,
    email: person.email,
    firstName: person.prenom,
    idRole: [],
    idSociete: idSociete,
    idUserCreation: 0,
    lastName: person.nom,
    phone: person.numPhone,
    pwd: person.mdp,
    rePWD: person.mdp,
    fileUrl: fileUrl,
  });

  // the message of an access creation that did not succeed
  const accessError = (data, who) => {
    if (data.errorExist) return who + " : cet email est déjà utilisé";
    if (data.errorPassword) return who + " : les mots de passe ne correspondent pas";
    return who + (data.error ? " : " + data.error : " : échec");
  };

  const finish = () => {
    setFinished(true);
    setBusy(false);
    setTimeout(() => {
      setIsCancel(false);
      setOnConfirmAddSociete(false);
    }, 1500);
  };

  const run = async () => {
    const p = progress.current;
    const retry = " — cliquez sur « Réessayer » pour continuer sans recréer la société";

    // nothing is created while an email is already used or invalid
    if (p.idSociete) {
      mark("emails", "done");
    } else {
      begin("emails");
      const emails = [admin.email];
      if (defaults.manager) emails.push(defaults.manager.email);
      for (const email of emails) {
        const res = await SocieteAndUserService.isEmailAvailable(email);
        if (!res.data.available) {
          fail(
            "emails",
            "L'email " +
              email +
              (res.data.reason === "email invalide" ? " n'est pas valide" : " est déjà utilisé") +
              " : rien n'a été créé"
          );
          return;
        }
      }
      mark("emails", "done");
    }

    // the picture is sent once: logo of the company and photo of its accesses
    if (p.fileUrl) {
      mark("image", "done");
    } else {
      begin("image");
      const res = await uploadPicture();
      if (Object.keys(res.data)[0] !== "success") {
        fail("image", "L'image de la société n'a pas pu être envoyée");
        return;
      }
      p.fileUrl = res.data.success;
      mark("image", "done");
    }

    if (p.idSociete) {
      mark("societe", "done");
    } else {
      begin("societe");
      const res = await SocieteAndUserService.addSociete({
        title: confirmSocieteModel.title,
        matricule: confirmSocieteModel.matricule,
        adresse: confirmSocieteModel.adresse,
        idUserCreation: 0,
        type: confirmSocieteModel.typeSociete,
        codeTVA: confirmSocieteModel.codeTVA,
        codeCategorie: confirmSocieteModel.codeCategorie,
        numberEtabSecondaire: confirmSocieteModel.numberEtabSecondaire,
        email: confirmSocieteModel.email,
        numPhone: confirmSocieteModel.numPhone,
        typeTVA: confirmSocieteModel.typeTVA,
        timbre: confirmSocieteModel.timbre,
        tva: confirmSocieteModel.tva,
        typeClient: confirmSocieteModel.typeClient,
        isServiceOnly: confirmSocieteModel.isServiceOnly,
        fileUrl: p.fileUrl,
      });
      if (!res.data.societeSuccess) {
        fail("societe", res.data.error || "La société n'a pas pu être ajoutée");
        return;
      }
      p.idSociete = res.data.societeSuccess.id;
      mark("societe", "done");
    }

    if (p.admin) {
      mark("admin", "done");
    } else {
      begin("admin");
      const res = await SocieteAndUserService.addIwareClient(accessModel(admin, p.idSociete, p.fileUrl));
      if (!res.data.success) {
        fail("admin", "La société est créée, mais " + accessError(res.data, "l'accès admin") + retry);
        return;
      }
      p.admin = res.data.success;
      mark("admin", "done");
    }

    if (defaults.manager) {
      if (p.managerDone) {
        mark("manager", "done");
      } else {
        begin("manager");
        const res = await SocieteAndUserService.addIwareWarehouseManager(
          accessModel(defaults.manager, p.idSociete, p.fileUrl)
        );
        if (!res.data.success) {
          fail(
            "manager",
            "La société et l'accès admin sont créés, mais " + accessError(res.data, "l'accès responsable dépôt") + retry
          );
          return;
        }
        p.idManager = res.data.success.id;
        p.managerDone = true;
        mark("manager", "done");
      }
    }

    if (defaults.centralWarehouse || defaults.defaultCatalog) {
      begin("defaults");
      const res = await SocieteAndUserService.initSocieteDefaults({
        idUser: p.admin.id,
        centralWarehouse: !!defaults.centralWarehouse,
        defaultCatalog: !!defaults.defaultCatalog,
        idWarehouseManager: p.idManager,
      });
      const problems = (res.data.error ? [res.data.error] : []).concat(res.data.errors || []);
      if (problems.length > 0) {
        fail("defaults", "La société et ses accès sont créés, mais : " + problems.join(" ; ") + retry);
        return;
      }
      mark("defaults", "done");
    }
    finish();
  };

  const handleConfirm = () => {
    if (busy || finished) return;
    setBusy(true);
    setErrorText("");
    run().catch((e) => {
      if (currentStep.current) mark(currentStep.current, "error");
      setBusy(false);
      setErrorText(
        (progress.current.idSociete ? "La société est créée, mais une étape a échoué : " : "") +
          messageOf(e) +
          (progress.current.idSociete ? " — cliquez sur « Réessayer » pour continuer sans recréer la société" : "")
      );
    });
  };

  const m = confirmSocieteModel;

  return (
    <Dialog
      open={true}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <div className="modal-header" style={{ borderBottom: `3px solid ${GREEN}` }}>
        <div>
          <h5 className="modal-title font-weight-bold">
            <i className="fas fa-clipboard-check mr-2" style={{ color: GREEN }} />
            Confirmer l'ajout de la société
          </h5>
          <div className="text-muted small">
            Vérifiez les informations ci-dessous : la société, son accès administrateur et la configuration demandée
            seront créés.
          </div>
        </div>
      </div>

      <div className="modal-body" style={{ overflowY: "auto" }}>
        {finished ? (
          <div className="text-center py-4">
            <i className="fas fa-check-circle fa-3x mb-3" style={{ color: GREEN }} />
            <h5>Société et accès créés avec succès</h5>
            <div className="text-muted">{m.title}</div>
          </div>
        ) : (
          <>
            {started && (
              <div className="mb-3" aria-live="polite">
                <ul className="list-unstyled mb-0">
                  {stepList.map((step) => (
                    <li key={step.key} className="py-1">
                      <span className="mr-2">{STEP_ICON[steps[step.key] || "pending"]}</span>
                      <span className={steps[step.key] === "error" ? "text-danger font-weight-bold" : ""}>
                        {step.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {errorText && (
              <div className="alert alert-danger" role="alert">
                <i className="fas fa-exclamation-triangle mr-2" />
                {errorText}
              </div>
            )}

            {!started && (
              <div className="row">
                <div className="col-md-6">
                  <Section icon="fa-building" title="Société">
                    <div className="d-flex align-items-center mb-2">
                      {logoUrl && (
                        <img
                          src={logoUrl}
                          alt="Logo de la société"
                          className="rounded border mr-3"
                          style={{ height: 56, maxWidth: 96, objectFit: "contain", backgroundColor: "#fff" }}
                        />
                      )}
                      <div>
                        <div className="h6 mb-0">{m.title}</div>
                        <span className="badge badge-success mr-1">{m.typeClient === "CLIENT" ? "Client" : m.typeClient}</span>
                        {m.isServiceOnly === 1 && <span className="badge badge-secondary">Société de service</span>}
                      </div>
                    </div>
                    <Info label="Matricule" value={m.matricule} />
                    <Info label="Email" value={m.email} />
                    <Info label="Téléphone" value={m.numPhone} />
                    <Info label="Adresse" value={m.adresse} />
                    <Info label="Établissements secondaires" value={m.numberEtabSecondaire} />
                    <Info label="Code de catégorie" value={m.codeCategorie} />
                    {m.codeCategorie === "C" && <Info label="Type de société" value={m.typeSociete} />}
                    <Info label="Code TVA" value={m.codeTVA} />
                    <Info label="Type de TVA" value={m.typeTVA} />
                    <Info label="TVA ( % )" value={m.tva} />
                    <Info label="Timbre fiscal" value={m.timbre} />
                  </Section>
                </div>
                <div className="col-md-6">
                  <Section icon="fa-user-shield" title="Accès administrateur">
                    <Info label="Nom" value={`${admin.prenom} ${admin.nom}`} />
                    <Info label="CIN" value={admin.cin} />
                    <Info label="Email (identifiant)" value={admin.email} />
                    <Info label="Téléphone" value={admin.numPhone} />
                    <Info label="Mot de passe" value="•••••••• (défini)" />
                  </Section>

                  <Section icon="fa-cogs" title="Configuration par défaut">
                    {defaults.centralWarehouse || defaults.defaultCatalog || defaults.manager ? (
                      <ul className="list-unstyled mb-0">
                        {defaults.centralWarehouse && (
                          <li className="py-1">
                            <i className="fas fa-check mr-2" style={{ color: GREEN }} />
                            Dépôt central
                          </li>
                        )}
                        {defaults.manager && (
                          <li className="py-1">
                            <i className="fas fa-check mr-2" style={{ color: GREEN }} />
                            Accès responsable dépôt : {defaults.manager.prenom} {defaults.manager.nom}
                            <div className="text-muted small ml-4">{defaults.manager.email}</div>
                          </li>
                        )}
                        {defaults.defaultCatalog && (
                          <li className="py-1">
                            <i className="fas fa-check mr-2" style={{ color: GREEN }} />
                            Famille, sous-famille, catégorie et marque « DEFAUT »
                          </li>
                        )}
                      </ul>
                    ) : (
                      <div className="text-muted">Aucune : la société démarre vide.</div>
                    )}
                  </Section>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {!finished && (
        <div className="modal-footer">
          <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={busy}>
            {somethingCreated ? "Fermer" : "Annuler"}
          </button>
          <button type="button" className="btn btn-success" onClick={handleConfirm} disabled={busy}>
            {busy ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2" />
                Création en cours...
              </>
            ) : started ? (
              <>
                <i className="fas fa-redo mr-2" />
                Réessayer
              </>
            ) : (
              <>
                <i className="fas fa-check mr-2" />
                Confirmer et créer
              </>
            )}
          </button>
        </div>
      )}
    </Dialog>
  );
};

export default ConfirmAddSocieteWithAdmin;
